/**
 * **The rolled item record** (PRD #795).
 *
 * An item used to be `{ id, baseId }`, which meant two drops of the same base
 * were the same item forever. An {@link ArpgItem} is a *roll*: a base, an item
 * level, a material tier, a rarity, and the affixes it rolled with their tier
 * and their rolled value. Two Rare plate chests are different items, and a
 * reported drop is reproducible from the seed and the drop position that made
 * it.
 *
 * **What an item contributes is computed, never stored.** The record holds what
 * was rolled; {@link arpgItemContribution} turns it into the equipment stage of
 * the derivation fold every time it is asked. That is what lets the tuning PRD
 * (#799) move a material multiplier or an attribute scalar and have every
 * already-owned item move with it, and it is asserted by a test rather than
 * left as an intention.
 *
 * **A rune is representable without shipping runes.** `runeTier` is an optional
 * field rather than part of an item's identity, so the fusion ladder proposed
 * on #713 is additive later with no migration. Nothing rolls it today.
 */

import type { DamageType } from '../../combat/abilityCatalog';
import type { EquipmentContribution } from '../../combat/derivedStats';
import type { ArpgClassId } from '../classes';
import {
  affixBand,
  affixName,
  arpgAffix,
  type ArpgAffixDefinition,
} from './affixes';
import {
  arpgItemBase,
  baseAllowsClass,
  type ArpgItemBase,
  type ArpgItemSlot,
} from './bases';
import {
  arpgMaterial,
  arpgMaterialImplicit,
  isArpgMaterialTier,
  type ArpgMaterialTier,
} from './materials';

/** The three rarities. Sets, uniques, and legendaries are out of scope. */
export const ARPG_RARITIES = ['common', 'magic', 'rare'] as const;

export type ArpgRarity = (typeof ARPG_RARITIES)[number];

/** How many affixes each rarity carries. Magic takes at most one of each kind. */
export const ARPG_RARITY_AFFIX_COUNTS: Readonly<
  Record<ArpgRarity, { min: number; max: number }>
> = {
  common: { min: 0, max: 0 },
  magic: { min: 1, max: 2 },
  rare: { min: 3, max: 5 },
};

/** One rolled affix: which affix, which tier's band it rolled in, and the value. */
export interface ArpgItemAffix {
  affixId: string;
  tier: number;
  value: number;
}

/** One item, as it exists on the ground, in a backpack, and in a stash. */
export interface ArpgItem {
  id: string;
  baseId: string;
  /** The drop position that made it, which is also its level requirement. */
  itemLevel: number;
  material: ArpgMaterialTier;
  rarity: ArpgRarity;
  affixes: ArpgItemAffix[];
  /**
   * A rune's tier, for the fusion ladder proposed on #713. A field rather than
   * part of the identity, so fusion is additive with no migration. Nothing
   * rolls this today and no consumer reads it.
   */
  runeTier?: number;
}

export function isArpgRarity(value: unknown): value is ArpgRarity {
  return (
    typeof value === 'string' && (ARPG_RARITIES as readonly string[]).includes(value)
  );
}

/**
 * Whether a value is a well-formed item.
 *
 * Strict, because this is what the account store admits into durable state and
 * what the run trusts on the way back out. An unknown base, an unknown material,
 * an unknown affix, or an affix on a slot that cannot carry it is a rejected
 * record rather than a silently half-applied one.
 */
export function isArpgItem(value: unknown): value is ArpgItem {
  if (value === null || typeof value !== 'object') return false;
  const item = value as Partial<ArpgItem>;
  if (
    typeof item.id !== 'string' ||
    item.id.length === 0 ||
    item.id.length > 160 ||
    typeof item.baseId !== 'string' ||
    typeof item.itemLevel !== 'number' ||
    !Number.isSafeInteger(item.itemLevel) ||
    item.itemLevel < 1 ||
    !isArpgMaterialTier(item.material) ||
    !isArpgRarity(item.rarity) ||
    !Array.isArray(item.affixes)
  ) {
    return false;
  }
  const base = arpgItemBase(item.baseId);
  if (!base) return false;
  if (!base.ladder && item.material !== 'normal') return false;
  if (item.runeTier !== undefined) {
    if (!Number.isSafeInteger(item.runeTier) || item.runeTier < 1) return false;
  }
  const counts = ARPG_RARITY_AFFIX_COUNTS[item.rarity];
  if (item.affixes.length < counts.min || item.affixes.length > counts.max) {
    return false;
  }
  const seen = new Set<string>();
  let prefixes = 0;
  for (const affix of item.affixes) {
    if (affix === null || typeof affix !== 'object') return false;
    const definition = arpgAffix(affix.affixId);
    if (
      !definition ||
      !definition.slots.has(base.slot) ||
      seen.has(affix.affixId) ||
      !Number.isSafeInteger(affix.tier) ||
      affix.tier < 1 ||
      affix.tier > definition.bands.length ||
      typeof affix.value !== 'number' ||
      !Number.isFinite(affix.value)
    ) {
      return false;
    }
    seen.add(affix.affixId);
    if (definition.kind === 'prefix') prefixes += 1;
  }
  // Magic is one prefix and one suffix at most, which is a shape rule rather
  // than a count rule: two prefixes on a Magic item is not a Magic item.
  if (item.rarity === 'magic') {
    const suffixes = item.affixes.length - prefixes;
    if (prefixes > 1 || suffixes > 1) return false;
  }
  return true;
}

/**
 * Author one item directly, without a roll.
 *
 * The roller is how a drop is made; this is how a *specific* item is named, for
 * a fixture, a scenario, or authored starting gear. It defaults to the bottom
 * of both axes, so an item with nothing said about it is the plainest possible
 * example of its base, and it returns null for an unknown base rather than
 * inventing one.
 */
export function createArpgItem(
  id: string,
  baseId: string,
  options: {
    itemLevel?: number;
    material?: ArpgMaterialTier;
    rarity?: ArpgRarity;
    affixes?: readonly ArpgItemAffix[];
  } = {},
): ArpgItem | null {
  const base = arpgItemBase(baseId);
  if (!base) return null;
  return {
    id,
    baseId: base.id,
    itemLevel: Math.max(1, Math.round(options.itemLevel ?? 1)),
    material: base.ladder ? options.material ?? 'normal' : 'normal',
    rarity: options.rarity ?? 'common',
    affixes: (options.affixes ?? []).map((affix) => ({ ...affix })),
  };
}

/** The base this item was rolled from, or null for an unknown base. */
export function arpgItemBaseOf(item: ArpgItem): ArpgItemBase | null {
  return item && typeof item === 'object' ? arpgItemBase(item.baseId) : null;
}

/** The slot this item fills. */
export function arpgItemSlot(item: ArpgItem): ArpgItemSlot | null {
  return arpgItemBaseOf(item)?.slot ?? null;
}

/**
 * The Hero level this item asks for.
 *
 * The drop position is the requirement, floored at whatever the base itself
 * asks: a Normal plate chest that dropped at item level one still needs the
 * level plate asks for, because the family gate is content and the item level
 * gate is depth.
 */
export function arpgItemRequiredLevel(item: ArpgItem): number {
  const base = arpgItemBaseOf(item);
  if (!base) return Number.POSITIVE_INFINITY;
  return Math.max(base.minLevel, item.itemLevel);
}

/** Whether this class may wear this item at all, ignoring level. */
export function arpgItemAllowsClass(item: ArpgItem, classId: ArpgClassId): boolean {
  const base = arpgItemBaseOf(item);
  return base ? baseAllowsClass(base, classId) : false;
}

/** The implicit stat this item carries, after its material tier multiplies it. */
export function arpgItemImplicit(
  item: ArpgItem,
): { kind: ArpgItemBase['implicit']['kind']; value: number } | null {
  const base = arpgItemBaseOf(item);
  if (!base) return null;
  return {
    kind: base.implicit.kind,
    value: base.ladder
      ? arpgMaterialImplicit(item.material, base.implicit.value)
      : base.implicit.value,
  };
}

/** The value one rolled affix contributes, clamped into its declared band. */
function affixValue(definition: ArpgAffixDefinition, affix: ArpgItemAffix): number {
  const band = affixBand(definition, affix.tier);
  const low = Math.min(band.min, band.max);
  const high = Math.max(band.min, band.max);
  return Math.min(high, Math.max(low, affix.value));
}

/**
 * What this item contributes to the derivation fold.
 *
 * Computed on every call, never stored. The implicit enters first, then the
 * family trait, then every rolled affix through the channel it declared. The
 * material tier is applied to the implicit alone: a test proves an affix's
 * rolled value is byte-identical at Normal and at Valorite.
 */
export function arpgItemContribution(item: ArpgItem): EquipmentContribution {
  const base = arpgItemBaseOf(item);
  if (!base) return {};
  const attributes: Record<string, number> = {};
  const flat: Record<string, number> = {};
  const resists: Partial<Record<DamageType, number>> = {};
  const percent: Record<string, number> = {};

  const implicit = arpgItemImplicit(item);
  if (implicit && implicit.value > 0) {
    if (implicit.kind === 'armor') {
      resists.physical = (resists.physical ?? 0) + implicit.value;
    } else if (implicit.kind === 'damage') {
      flat.mainHandDamage = (flat.mainHandDamage ?? 0) + implicit.value;
    } else {
      flat.maxMana = (flat.maxMana ?? 0) + implicit.value;
    }
  }

  // The family signature. Outside the ladder by design: it is the same at
  // Normal and at Valorite, which is what keeps the family axis meaningful.
  const trait = base.trait;
  if (trait) {
    if (trait.attackSpeedPercent !== undefined) {
      percent.attackSpeed = (percent.attackSpeed ?? 0) + trait.attackSpeedPercent;
    }
    if (trait.castSpeedPercent !== undefined) {
      percent.castSpeed = (percent.castSpeed ?? 0) + trait.castSpeedPercent;
    }
    if (trait.maxManaFlat !== undefined) {
      flat.maxMana = (flat.maxMana ?? 0) + trait.maxManaFlat;
    }
    if (trait.maxHealthFlat !== undefined) {
      flat.maxHealth = (flat.maxHealth ?? 0) + trait.maxHealthFlat;
    }
    if (trait.critChance !== undefined) {
      flat.critChance = (flat.critChance ?? 0) + trait.critChance;
    }
  }

  for (const affix of item.affixes ?? []) {
    const definition = arpgAffix(affix.affixId);
    if (!definition) continue;
    const value = affixValue(definition, affix);
    const contribution = definition.contribution;
    if (contribution.channel === 'attribute') {
      attributes[contribution.key] = (attributes[contribution.key] ?? 0) + value;
    } else if (contribution.channel === 'flat') {
      flat[contribution.key] = (flat[contribution.key] ?? 0) + value;
    } else if (contribution.channel === 'resist') {
      resists[contribution.damageType] =
        (resists[contribution.damageType] ?? 0) + value;
    } else {
      percent[contribution.key] = (percent[contribution.key] ?? 0) + value;
    }
  }

  const flatContribution = {
    ...flat,
    ...(Object.keys(resists).length > 0 ? { resists } : {}),
  };
  return {
    ...(Object.keys(attributes).length > 0
      ? { attributes: attributes as EquipmentContribution['attributes'] }
      : {}),
    ...(Object.keys(flatContribution).length > 0
      ? { flat: flatContribution as EquipmentContribution['flat'] }
      : {}),
    ...(Object.keys(percent).length > 0
      ? { percent: percent as EquipmentContribution['percent'] }
      : {}),
  };
}

/**
 * The item's full name.
 *
 * Classic order: the prefix word, the material, the base, and the suffix, so
 * "Vanquishing Valorite Broadsword of Fortification" reads the way it always
 * has. Normal material and ladderless jewellery contribute no word, which is
 * what keeps a common drop's name short.
 */
export function arpgItemName(item: ArpgItem): string {
  const base = arpgItemBaseOf(item);
  if (!base) return 'Unknown item';
  const prefix = namedAffix(item, 'prefix');
  const suffix = namedAffix(item, 'suffix');
  const material =
    base.ladder && item.material !== 'normal' ? arpgMaterial(item.material).name : '';
  const head = [prefix, material, base.name].filter((part) => part !== '').join(' ');
  return suffix === '' ? head : `${head} of ${suffix}`;
}

/** The word one kind of affix lends the name, or an empty string for none. */
function namedAffix(item: ArpgItem, kind: 'prefix' | 'suffix'): string {
  for (const affix of item.affixes ?? []) {
    const definition = arpgAffix(affix.affixId);
    if (definition?.kind === kind) return affixName(definition, affix.tier);
  }
  return '';
}

/** One readable line of an item's tooltip. */
export interface ArpgItemLine {
  label: string;
  value: string;
  /** Present for a rolled affix, so a tooltip can show which tier it rolled. */
  tier?: number;
}

/** How a rolled value reads, at the precision its affix declared. */
function formatValue(definition: ArpgAffixDefinition, value: number): string {
  const precision = definition.precision ?? 0;
  const rounded = value.toFixed(precision);
  const unit = definition.unit ?? '';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${rounded}${unit}`;
}

const IMPLICIT_LABELS: Readonly<Record<ArpgItemBase['implicit']['kind'], string>> = {
  armor: 'Armor',
  damage: 'Damage',
  mana: 'Mana',
};

/**
 * Everything a tooltip needs to explain an item without a wiki (user story
 * three).
 *
 * Derived here rather than in the client, so the ground label, the backpack
 * row, and the stash row can never disagree about what an item is.
 */
export function arpgItemTooltip(item: ArpgItem): {
  name: string;
  rarity: ArpgRarity;
  slot: ArpgItemSlot | null;
  requiredLevel: number;
  lines: ArpgItemLine[];
} {
  const base = arpgItemBaseOf(item);
  const lines: ArpgItemLine[] = [];
  const implicit = arpgItemImplicit(item);
  if (base && implicit && implicit.value > 0) {
    lines.push({
      label: IMPLICIT_LABELS[implicit.kind],
      value: `${implicit.value}`,
    });
  }
  if (base?.trait) {
    for (const [label, value, unit] of [
      ['Attack speed', base.trait.attackSpeedPercent, '%'],
      ['Cast speed', base.trait.castSpeedPercent, '%'],
      ['Mana', base.trait.maxManaFlat, ''],
      ['Health', base.trait.maxHealthFlat, ''],
      ['Crit chance', base.trait.critChance, ''],
    ] as const) {
      if (value === undefined) continue;
      lines.push({ label, value: `${value >= 0 ? '+' : ''}${value}${unit}` });
    }
  }
  for (const affix of item.affixes ?? []) {
    const definition = arpgAffix(affix.affixId);
    if (!definition) continue;
    lines.push({
      label: affixName(definition, affix.tier),
      value: formatValue(definition, affixValue(definition, affix)),
      tier: affix.tier,
    });
  }
  return {
    name: arpgItemName(item),
    rarity: item.rarity,
    slot: base?.slot ?? null,
    requiredLevel: arpgItemRequiredLevel(item),
    lines,
  };
}

/**
 * One item's whole contribution, flattened into readable, comparable numbers.
 *
 * Derived from the same {@link arpgItemContribution} the fold reads, so a
 * comparison can never claim a number the derivation would not produce. The
 * keys are labels rather than field paths, because two channels can land on the
 * same player-visible stat and a comparison that showed them separately would
 * be arithmetic the player has to finish.
 */
export function arpgItemStatSheet(item: ArpgItem): Map<string, number> {
  const sheet = new Map<string, number>();
  const add = (label: string, value: number): void => {
    if (value === 0) return;
    sheet.set(label, (sheet.get(label) ?? 0) + value);
  };
  const contribution = arpgItemContribution(item);
  for (const [key, value] of Object.entries(contribution.attributes ?? {})) {
    add(key.charAt(0).toUpperCase() + key.slice(1), value ?? 0);
  }
  const flat = contribution.flat ?? {};
  add('Damage', flat.mainHandDamage ?? 0);
  add('Health', flat.maxHealth ?? 0);
  add('Mana', flat.maxMana ?? 0);
  add('Health regen', flat.healthRegenPerTick ?? 0);
  add('Mana regen', flat.manaRegenPerTick ?? 0);
  add('Crit chance', flat.critChance ?? 0);
  for (const [type, value] of Object.entries(flat.resists ?? {})) {
    add(`${type.charAt(0).toUpperCase() + type.slice(1)} resist`, value ?? 0);
  }
  const percent = contribution.percent ?? {};
  add('Physical damage %', percent.physicalDamage ?? 0);
  add('Spell damage %', percent.spellDamage ?? 0);
  add('Attack speed %', percent.attackSpeed ?? 0);
  add('Cast speed %', percent.castSpeed ?? 0);
  add('Health %', percent.maxHealth ?? 0);
  add('Mana %', percent.maxMana ?? 0);
  return sheet;
}

/**
 * What swapping one item for another actually changes (user story four).
 *
 * Every label either item touches, with the difference the swap makes. An
 * absent equipped item compares against nothing, which is the first item in a
 * slot rather than a special case.
 */
export function arpgItemComparison(
  candidate: ArpgItem,
  equipped: ArpgItem | null,
): { label: string; delta: number }[] {
  const left = arpgItemStatSheet(candidate);
  const right = equipped ? arpgItemStatSheet(equipped) : new Map<string, number>();
  const labels = [...new Set([...left.keys(), ...right.keys()])];
  return labels
    .map((label) => ({
      label,
      delta: Number(((left.get(label) ?? 0) - (right.get(label) ?? 0)).toFixed(3)),
    }))
    .filter((entry) => entry.delta !== 0);
}

/** The hue this item's equipped layer is dyed, or zero for raw art. */
export function arpgItemHue(item: ArpgItem): number {
  const base = arpgItemBaseOf(item);
  if (!base?.ladder) return 0;
  return arpgMaterial(item.material).hue;
}
